import ApplicationController from './application_controller'

// Shows a cell's explanation on hover and on focus, and places it.
//
// The table scrolls inside its own container, and `overflow-x: auto` forces the
// other axis to `auto` too, so a box anchored to the cell is clipped on the rows
// near the bottom. The explanation is a popover instead: it renders in the top
// layer, outside every overflow and stacking context, which is also why its
// position has to be computed rather than inherited.
export default class extends ApplicationController {
  static targets = ['tip']

  static MARGIN = 8

  connect () {
    this.hovered = false
    this.focused = false
    // A popover carries viewport coordinates, so anything that moves the cell
    // under it leaves it behind. Capture: the table scrolls in its own
    // container, and a scroll event there does not bubble.
    this.onViewportChange = () => this.followsKeyboard() ? this.place() : this.close()
    addEventListener('scroll', this.onViewportChange, true)
    addEventListener('resize', this.onViewportChange)
  }

  disconnect () {
    removeEventListener('scroll', this.onViewportChange, true)
    removeEventListener('resize', this.onViewportChange)
    this.close()
  }

  // The two triggers are independent: clicking a cell focuses it, and moving
  // the mouse away then closed an explanation the keyboard still had open.
  show (event) {
    this[event.type === 'focus' ? 'focused' : 'hovered'] = true
    this.open()
  }

  hide (event) {
    this[event.type === 'blur' ? 'focused' : 'hovered'] = false
    if (this.hovered || this.focused) return

    this.close()
  }

  // Escape is what the WAI-ARIA tooltip pattern expects, and a manual popover
  // opts out of the light dismiss that would otherwise provide it.
  dismiss () {
    this.hovered = this.focused = false
    this.close()
  }

  // `togglePopover(force)` rather than showPopover: tabbing onto a cell the
  // mouse already sits on fires both triggers, and showing an open popover
  // throws.
  open () {
    this.tipTarget.togglePopover(true)
    this.place()
  }

  // Only an open popover is closed. A frame refresh removes every cell before
  // their controllers disconnect, and toggling a popover that has left the
  // document throws — hundreds of times per refresh, one per cell. The browser
  // has already hidden it by then, so it never matches :popover-open.
  close () {
    if (!this.hasTipTarget || !this.tipTarget.matches(':popover-open')) return

    this.tipTarget.hidePopover()
  }

  // Tabbing onto a cell out of view scrolls the table to it, and closing on that
  // scroll shut the explanation the keyboard had just opened. Only a keyboard
  // focus, though: a click focuses the cell too, and only while the cell is
  // still on screen, not once the reader has scrolled it out of the table.
  followsKeyboard () {
    if (!this.element.matches(':focus-visible')) return false

    const { left, top, width, height } = this.element.getBoundingClientRect()
    return this.element.contains(document.elementFromPoint(left + width / 2, top + height / 2))
  }

  // Below the cell, unless the viewport has no room for it there, and never off
  // the side.
  place () {
    const margin = this.constructor.MARGIN
    const cell = this.element.getBoundingClientRect()
    const tip = this.tipTarget.getBoundingClientRect()

    const below = cell.bottom + margin
    const fits = below + tip.height <= innerHeight
    const top = fits ? below : Math.max(margin, cell.top - tip.height - margin)
    const left = Math.min(Math.max(margin, cell.left), innerWidth - tip.width - margin)

    this.tipTarget.style.top = `${top}px`
    this.tipTarget.style.left = `${left}px`
  }
}
